import { FC, useCallback, useEffect, useState } from 'react';

import { getChannelLedger } from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import {
  ChannelLedgerListResponse,
  DEDUCT_SOURCE_LABEL,
  GiftChannelLedgerEntry,
  GiftChannelLedgerType,
  LEDGER_TYPE_CONFIG,
  ledgerGiftItemNames,
} from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Link } from 'react-router-dom';

import {
  kstDateOf,
  kstDayEndISO,
  kstShiftDay,
  kstTimeHHmm,
  kstToday,
} from '../../utils/kstTime.utils';

interface Props {
  initialChannel: DeliveryChannel;
  onClose: () => void;
}

/** 기준일 자정(KST) − 12시간 = 전날 12:00(정오, KST) — 조회 시작점 */
const prevNoonISO = (date: string) =>
  new Date(new Date(`${date}T00:00:00+09:00`).getTime() - 12 * 3600_000).toISOString();

/** 'YYYY-MM-DD' → 'M/D' */
const formatDayLabel = (date: string) => {
  const [, m, d] = date.split('-');
  if (!m || !d) return date;
  return `${Number(m)}/${Number(d)}`;
};

/** 기준일 건은 'HH:mm', 전날(12:00 이후) 건은 '전날 HH:mm' — 모두 KST 고정 */
const formatTime = (occurredAt: Date, baseDate: string) => {
  const d = new Date(occurredAt);
  const hhmm = kstTimeHHmm(d);
  return kstDateOf(d) === baseDate ? hhmm : `전날 ${hhmm}`;
};

const entryDetail = (entry: GiftChannelLedgerEntry): string => {
  const parts: string[] = [];
  const itemNames = ledgerGiftItemNames(entry);
  if (itemNames.length > 0) parts.push(itemNames.join(', '));
  if (entry.type === GiftChannelLedgerType.Deduct && entry.quantity) {
    parts.push(`${entry.quantity}건 × ${(entry.unitPriceWhole ?? 0).toLocaleString()}원`);
  }
  if (entry.source && entry.source !== 'deliver') {
    parts.push(DEDUCT_SOURCE_LABEL[entry.source]);
  }
  if (entry.memo) parts.push(entry.memo);
  return parts.join(' · ');
};

/**
 * 발송대기 페이지 "현재 잔액" 클릭 시 — 기준일(기본 오늘)의 잔액 변경을
 * 전날 12:00 잔액에서 시작해 통장 내역처럼 보여준다. 하루 단위로 이동 가능.
 * 무슨 상품을 누구에게 보냈는지 등 상세는 발송내역 조회 페이지로 넘긴다.
 */
export const ChannelBalanceChangesModal: FC<Props> = ({ initialChannel, onClose }) => {
  const [channel, setChannel] = useState<DeliveryChannel>(initialChannel);
  const [date, setDate] = useState<string>(kstToday());
  const [ledger, setLedger] = useState<ChannelLedgerListResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'done'>('loading');

  const isToday = date === kstToday();

  const fetchChanges = useCallback(async () => {
    setStatus('loading');
    try {
      setLedger(await getChannelLedger(channel, prevNoonISO(date), kstDayEndISO(date)));
      setStatus('done');
    } catch {
      setLedger(null);
      setStatus('error');
    }
  }, [channel, date]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-5 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">잔액 변경 내역</h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded px-1.5 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {DELIVERY_CHANNEL_LIST.map((ch) => {
              const config = DELIVERY_CHANNEL_CONFIG[ch];
              return (
                <button
                  key={ch}
                  type="button"
                  onClick={() => setChannel(ch)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                    config.badgeClass
                  } ${
                    channel === ch
                      ? 'ring-2 ring-indigo-300'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  {config.label}
                </button>
              );
            })}

            {/* 날짜 이동 — 기본 오늘, 하루 단위 */}
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => setDate(kstShiftDay(date, -1))}
                className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                ←
              </button>
              <input
                type="date"
                value={date}
                max={kstToday()}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs"
              />
              <button
                type="button"
                onClick={() => setDate(kstShiftDay(date, 1))}
                disabled={isToday}
                className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                →
              </button>
              <button
                type="button"
                onClick={() => setDate(kstToday())}
                disabled={isToday}
                className="rounded border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
              >
                오늘
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {status === 'loading' ? (
            <p className="py-6 text-center text-sm text-gray-400">불러오는 중…</p>
          ) : status === 'error' || !ledger ? (
            <p className="py-6 text-center text-sm text-red-500">
              변경 내역을 불러오지 못했습니다.
            </p>
          ) : (
            <>
              <div className="flex items-baseline justify-between border-b pb-1.5 text-xs">
                <span className="text-gray-500">
                  전날({formatDayLabel(kstShiftDay(date, -1))}) 12:00 잔액
                </span>
                <span className="font-semibold text-gray-900">
                  {ledger.openingBalance.toLocaleString()}원
                </span>
              </div>

              {ledger.entries.length > 0 &&
                (() => {
                  // 기간 합계 — 충전(+ 항목 합) / 발송·사용(− 항목 합)을 한눈에
                  const totalIn = ledger.entries
                    .filter((entry) => entry.amount > 0)
                    .reduce((sum, entry) => sum + entry.amount, 0);
                  const totalOut = ledger.entries
                    .filter((entry) => entry.amount < 0)
                    .reduce((sum, entry) => sum + entry.amount, 0);
                  const net = totalIn + totalOut;
                  return (
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5 border-b py-1 text-[11px]">
                      <span className="text-gray-400">합계</span>
                      <span className="font-semibold text-emerald-600">
                        충전 +{totalIn.toLocaleString()}원
                      </span>
                      <span className="font-semibold text-red-600">
                        발송·사용 −{Math.abs(totalOut).toLocaleString()}원
                      </span>
                      <span
                        className={`font-semibold ${
                          net < 0 ? 'text-red-600' : 'text-gray-700'
                        }`}
                      >
                        순변동 {net > 0 ? '+' : ''}
                        {net.toLocaleString()}원
                      </span>
                    </div>
                  );
                })()}

              {ledger.entries.length === 0 ? (
                <p className="py-4 text-center text-xs text-gray-400">
                  이후 변경이 없습니다.
                </p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {ledger.entries.map((entry) => {
                    const typeConfig = LEDGER_TYPE_CONFIG[entry.type];
                    const detail = entryDetail(entry);
                    return (
                      <div key={entry._id} className="flex items-center gap-1.5 py-1">
                        <span className="w-14 shrink-0 text-[11px] text-gray-400">
                          {formatTime(entry.occurredAt, date)}
                        </span>
                        <span
                          className={`shrink-0 rounded-full border px-1.5 text-[10px] font-medium leading-4 ${typeConfig.badgeClass}`}
                        >
                          {typeConfig.label}
                        </span>
                        <span
                          className="min-w-0 flex-1 truncate text-[11px] text-gray-500"
                          title={detail}
                        >
                          {detail}
                        </span>
                        <span
                          className={`shrink-0 text-[11px] font-semibold ${
                            entry.amount < 0 ? 'text-red-600' : 'text-emerald-600'
                          }`}
                        >
                          {entry.amount > 0 ? '+' : ''}
                          {entry.amount.toLocaleString()}
                        </span>
                        <span className="w-[76px] shrink-0 text-right text-[11px] text-gray-400">
                          {(entry.balanceAfter ?? 0).toLocaleString()}원
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-baseline justify-between border-t pt-1.5 text-xs">
                <span className="text-gray-500">
                  {isToday ? '현재 잔액' : `${formatDayLabel(date)} 종료 잔액`}
                </span>
                <span
                  className={`font-bold ${
                    ledger.closingBalance < 0 ? 'text-red-600' : 'text-indigo-700'
                  }`}
                >
                  {ledger.closingBalance.toLocaleString()}원
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-5 py-2">
          {/* 무슨 상품·어떤 번호로 발송했는지는 발송내역 조회에서 — 보던 날짜 그대로 이동 */}
          <Link
            to={`/gift-seller/delivered-history?date=${date}`}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
          >
            {formatDayLabel(date)} 발송내역 상세보기 →
          </Link>
          <Link
            to="/gift-seller/channel-ledger"
            className="text-xs text-indigo-500 hover:underline"
          >
            충전금 원장 전체 보기 →
          </Link>
        </div>
      </div>
    </div>
  );
};
